import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardActionArea,
  Grid,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';

interface DomainConnectionStatus {
  connected: boolean;
  needsReconnection: boolean;
  agencyName?: string;
  disconnectedAt?: string;
}

interface RealestateConfigStatus {
  configured: boolean;
  agentId?: string;
}

const ThirdPartyIntegrations: React.FC = () => {
  const { agencySlug } = useParams<{ agencySlug: string }>();
  const navigate = useNavigate();
  const { agency } = useAuth();
  const planName = (agency?.subscription?.planName || agency?.subscriptionPlan || 'Free').toLowerCase();
  const hasIntegrationAccess = ['professional', 'agency premium', 'premium', 'enterprise', 'demo'].includes(planName);
  const [domainStatus, setDomainStatus] = useState<DomainConnectionStatus | null>(null);
  const [loadingDomainStatus, setLoadingDomainStatus] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [isDomainSystemConfigured, setIsDomainSystemConfigured] = useState<boolean | null>(null);
  const [realestateStatus, setRealestateStatus] = useState<RealestateConfigStatus | null>(null);

  useEffect(() => {
    if (!hasIntegrationAccess || !agencySlug) return;
    
    // Check if Domain is configured on system side
    const checkDomainSystemConfig = async () => {
      try {
        const response = await api.get(`/agencies/${agencySlug}/domain-system-config-status`);
        setIsDomainSystemConfigured(response.data?.configured || false);
      } catch (err: any) {
        console.error('Error checking Domain system config:', err);
        // On error, assume not configured
        setIsDomainSystemConfigured(false);
      }
    };

    const fetchDomainStatus = async () => {
      try {
        setLoadingDomainStatus(true);
        const response = await api.get(`/agencies/${agencySlug}/domain-office-details`);
        if (response?.data) {
          setDomainStatus({
            connected: response.data.connected || false,
            needsReconnection: response.data.needsReconnection || false,
            agencyName: response.data.agencyName,
            disconnectedAt: response.data.disconnectedAt
          });
        } else {
          setDomainStatus({
            connected: false,
            needsReconnection: false
          });
        }
      } catch (err: any) {
        // Log error for debugging
        console.error('Error fetching Domain status:', err);
        console.error('Error response:', err.response?.data);
        // If endpoint doesn't exist or fails, assume not connected
        setDomainStatus({
          connected: false,
          needsReconnection: false
        });
      } finally {
        setLoadingDomainStatus(false);
      }
    };

    const fetchRealestateStatus = async () => {
      try {
        const response = await api.get(`/agencies/${agencySlug}/realestate-config`);
        const config = response?.data?.config;
        if (config?.agentId) {
          setRealestateStatus({
            configured: true,
            agentId: config.agentId
          });
        } else {
          setRealestateStatus({
            configured: false
          });
        }
      } catch (err: any) {
        // If endpoint doesn't exist or fails, assume not configured
        setRealestateStatus({
          configured: false
        });
      }
    };

    checkDomainSystemConfig();
    fetchDomainStatus();
    fetchRealestateStatus();
  }, [hasIntegrationAccess, agencySlug]);

  const handleDisconnect = async () => {
    if (!agencySlug || !window.confirm('Are you sure you want to disconnect Domain.com.au? This will stop automatic listing syncing.')) {
      return;
    }

    try {
      setDisconnecting(true);
      await api.post(`/agencies/${agencySlug}/domain-oauth/disconnect`);
      // Refresh status after disconnection
      const response = await api.get(`/agencies/${agencySlug}/domain-office-details`);
      if (response?.data) {
        setDomainStatus({
          connected: response.data.connected || false,
          needsReconnection: response.data.needsReconnection || false,
          agencyName: response.data.agencyName,
          disconnectedAt: response.data.disconnectedAt
        });
      } else {
        setDomainStatus({
          connected: false,
          needsReconnection: false
        });
      }
    } catch (err: any) {
      console.error('Error disconnecting Domain:', err);
      alert(err.response?.data?.message || 'Failed to disconnect Domain.com.au');
    } finally {
      setDisconnecting(false);
    }
  };

  if (!hasIntegrationAccess) {
    return (
      <Container maxWidth="md" sx={{ mt: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h4" component="h1" gutterBottom>
              3rd Party Integration
            </Typography>
            <Typography color="text.secondary">
              Automated Domain.com.au and RealEstate.com.au posting is available on the Agency Premium plan. Upgrade your subscription to connect third-party listing portals.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // Determine if Domain card should be disabled
  // Disabled when: connected AND NOT needs reconnection
  // Enabled when: not connected OR needs reconnection (refresh token invalid)
  const isDomainCardDisabled = domainStatus?.connected === true && domainStatus?.needsReconnection !== true;
  const canReconnectDomain = domainStatus?.connected === true && domainStatus?.needsReconnection === true;

  const isRealestateConfigured = realestateStatus?.configured === true;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        3rd Party Integrations
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Choose an integration to configure. Each integration requires separate credentials and setup.
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card 
            sx={{ 
              height: '100%',
              border: isRealestateConfigured ? '2px solid' : 'none',
              borderColor: isRealestateConfigured ? 'success.main' : 'transparent',
              position: 'relative'
            }}
          >
            <CardActionArea 
              onClick={() => navigate(`/agencies/${agencySlug}/integrations/realestate`)}
              sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Realestate.com.au
                  </Typography>
                  {isRealestateConfigured && (
                    <Chip 
                      icon={<CheckCircle />} 
                      label="Configured" 
                      color="success" 
                      size="small"
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Connect your realestate.com.au account to automatically post and update property listings. 
                  Features include:
                </Typography>
                <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Automated listing posting
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Feed status dashboard
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Credential validation
                  </Typography>
                  <Typography component="li" variant="body2" color="text.secondary">
                    Guided onboarding wizard
                  </Typography>
                </Box>
                <Button 
                  variant="contained" 
                  fullWidth
                  sx={{ mt: 'auto' }}
                >
                  {isRealestateConfigured ? 'View Details' : 'Configure Realestate.com.au'}
                </Button>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
        
        {/* Only show Domain.com.au card if system configuration exists */}
        {isDomainSystemConfigured !== false && (
          <Grid item xs={12} md={6}>
            <Card 
            sx={{ 
              height: '100%',
              border: isDomainCardDisabled ? '2px solid' : 'none',
              borderColor: isDomainCardDisabled ? 'success.main' : 'transparent',
              position: 'relative'
            }}
          >
            {loadingDomainStatus ? (
              <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </CardContent>
            ) : isDomainCardDisabled ? (
              // When connected, don't use CardActionArea - buttons handle navigation
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" component="h2" gutterBottom>
                        Domain.com.au
                      </Typography>
                      {domainStatus?.connected && !domainStatus?.needsReconnection && (
                        <Chip 
                          icon={<CheckCircle />} 
                          label="Connected" 
                          color="success" 
                          size="small"
                        />
                      )}
                      {canReconnectDomain && (
                        <Chip 
                          icon={<ErrorIcon />} 
                          label="Reconnect Required" 
                          color="warning" 
                          size="small"
                        />
                      )}
                    </Box>
                    {domainStatus?.connected && !domainStatus?.needsReconnection && domainStatus?.agencyName && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Connected to: {domainStatus.agencyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Integration is active. Visit the integration page to reconnect with a different account.
                        </Typography>
                      </Box>
                    )}
                    {canReconnectDomain && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Reconnection Required
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Your refresh token has expired. Please reconnect to continue syncing listings.
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Connect your domain.com.au account to automatically post and update property listings. 
                      Features include:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        OAuth2 authentication
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Automated listing posting
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Listing updates
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Agent ID configuration
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      {isDomainCardDisabled && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisconnect();
                          }}
                          disabled={disconnecting}
                          sx={{ flex: 1 }}
                        >
                          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      )}
                      <Button 
                        variant={canReconnectDomain ? "contained" : isDomainCardDisabled ? "outlined" : "contained"}
                        color={canReconnectDomain ? "warning" : isDomainCardDisabled ? "success" : "primary"}
                        fullWidth={!isDomainCardDisabled}
                        sx={{ 
                          flex: isDomainCardDisabled ? 1 : 'none',
                          pointerEvents: 'auto',
                          zIndex: 1,
                          position: 'relative'
                        }}
                        disabled={disconnecting}
                        onClick={(e) => {
                          e.stopPropagation(); // Always stop propagation to prevent CardActionArea from firing
                          e.preventDefault(); // Prevent any default behavior
                          navigate(`/agencies/${agencySlug}/integrations/domain`);
                        }}
                      >
                        {canReconnectDomain 
                          ? 'Reconnect Domain.com.au' 
                          : isDomainCardDisabled 
                          ? 'View Details' 
                          : 'Configure Domain.com.au'}
                      </Button>
                    </Box>
              </CardContent>
            ) : (
              // When disconnected or needs reconnection, use CardActionArea for card click navigation
              <CardActionArea
                onClick={() => {
                  navigate(`/agencies/${agencySlug}/integrations/domain`);
                }}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
              >
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h5" component="h2" gutterBottom>
                        Domain.com.au
                      </Typography>
                      {domainStatus?.connected && !domainStatus?.needsReconnection && (
                        <Chip 
                          icon={<CheckCircle />} 
                          label="Connected" 
                          color="success" 
                          size="small"
                        />
                      )}
                      {canReconnectDomain && (
                        <Chip 
                          icon={<ErrorIcon />} 
                          label="Reconnect Required" 
                          color="warning" 
                          size="small"
                        />
                      )}
                    </Box>
                    {domainStatus?.connected && !domainStatus?.needsReconnection && domainStatus?.agencyName && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Connected to: {domainStatus.agencyName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Integration is active. Visit the integration page to reconnect with a different account.
                        </Typography>
                      </Box>
                    )}
                    {canReconnectDomain && (
                      <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Reconnection Required
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Your refresh token has expired. Please reconnect to continue syncing listings.
                        </Typography>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Connect your domain.com.au account to automatically post and update property listings. 
                      Features include:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                      <Typography component="li" variant="body2" color="text.secondary">
                        OAuth2 authentication
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Automated listing posting
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Listing updates
                      </Typography>
                      <Typography component="li" variant="body2" color="text.secondary">
                        Agent ID configuration
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
                      {isDomainCardDisabled && (
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDisconnect();
                          }}
                          disabled={disconnecting}
                          sx={{ flex: 1 }}
                        >
                          {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                        </Button>
                      )}
                      <Button 
                        variant={canReconnectDomain ? "contained" : isDomainCardDisabled ? "outlined" : "contained"}
                        color={canReconnectDomain ? "warning" : isDomainCardDisabled ? "success" : "primary"}
                        fullWidth={!isDomainCardDisabled}
                        sx={{ 
                          flex: isDomainCardDisabled ? 1 : 'none',
                          pointerEvents: 'auto',
                          zIndex: 1,
                          position: 'relative'
                        }}
                        disabled={disconnecting}
                        onClick={(e) => {
                          e.stopPropagation(); // Always stop propagation to prevent CardActionArea from firing
                          e.preventDefault(); // Prevent any default behavior
                          navigate(`/agencies/${agencySlug}/integrations/domain`);
                        }}
                      >
                        {canReconnectDomain 
                          ? 'Reconnect Domain.com.au' 
                          : isDomainCardDisabled 
                          ? 'View Details' 
                          : 'Configure Domain.com.au'}
                      </Button>
                    </Box>
              </CardContent>
              </CardActionArea>
            )}
          </Card>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default ThirdPartyIntegrations;

